import { useEffect, useState } from "react";
import { AlertTriangleIcon, BriefcaseIcon, PlusIcon, XIcon } from "lucide-react";
import { Input } from "@component/input";
import { Textarea } from "@component/textarea";
import { WorkExperience as WorkExperienceType } from "@type/cv";
import { Button } from "@component/button";
import { cn } from "@util/tailwind";
import ExperienceTag, { filterTagsByContext, TagType } from "@component/experience-tag";

interface Props {
  isEditing: boolean;
  isDisabled: boolean;
  data: WorkExperienceType[];
  onChange: (values: WorkExperienceType[]) => void;
  onEdit: () => void;
}

export function WorkExperience(props: Props) {
  const [experiences, setExperiences] = useState<WorkExperienceType[]>([]);

  useEffect(() => {
    const experiencesData = props.data.map((experience) => ({
      ...experience,
      is_current: experience.period_end_year === 9999,
    }));
    setExperiences(experiencesData);
  }, []);

  useEffect(() => {
    const experiencesData = experiences.map((experience) => ({
      ...experience,
      period_start_month: Number(experience.period_start_month),
      period_start_year: Number(experience.period_start_year),
      period_end_month: Number(experience.period_end_month),
      period_end_year: Number(experience.period_end_year),
    }));
    props.onChange(experiencesData);
  }, [experiences]);

  const handleExperienceChange = (
    index: number,
    field: string,
    value: string
  ) => {
    const updatedExperiences = [...experiences];
    // @ts-ignore
    updatedExperiences[index][field] = value;
    setExperiences(updatedExperiences);
  };

  const addExperience = () => {
    setExperiences([
      ...experiences,
      {
        role: "",
        institution: "",
        location: "",
        period: "",
        description: "",
        period_start_month: 0,
        period_start_year: 0,
        period_end_month: 0,
        period_end_year: 9999,
        is_current: false,
        tags: []
      },
    ]);
  };

  const toggleCurrentlyWorking = (index: number, value: boolean) => {
    const updatedExperiences = [...experiences];
    updatedExperiences[index].is_current = value;
    updatedExperiences[index].period_end_year = updatedExperiences[index].is_current
      ? 9999
      : updatedExperiences[index].period_end_year;
    updatedExperiences[index].period_end_month = updatedExperiences[index].is_current
      ? 12
      : updatedExperiences[index].period_end_month;
    setExperiences(updatedExperiences);
  };


  const removeExperience = (index: number) => {
    setExperiences(experiences.filter((_, i) => i !== index));
  };

  return (
    <div className="sm:p-6 py-6">
      {!props.isEditing && experiences.length === 0 && (
        <Button
          onClick={props.onEdit}
          disabled={props.isDisabled}
          variant="ghost"
          className="flex items-center text-blue-500 hover:text-blue-700"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Work Experience
        </Button>
      )}

      <div>
        {experiences.map((job, index) => (
          <>
            {props.isEditing ? (
              <div className="space-y-4 mb-8">
                <button
                  onClick={() => removeExperience(index)}
                  className="float-right text-red-500 hover:text-red-700"
                >
                  <XIcon className="h-5 w-5" />
                </button>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-black mb-1 block">
                      Job Title
                    </label>
                    <Input
                      value={job.role}
                      onChange={(e) =>
                        handleExperienceChange(index, "role", e.target.value)
                      }
                      className="border-gray-300 text-black"
                    />
                    {job.role === "" && (
                      <div className="flex flex-row items-center mt-2 space-x-1">
                        <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                        <p className="text-red-600 text-sm">
                          Job title is required.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-black mb-1 block">
                      Period Start
                    </label>
                    <div className="flex flex-row space-x-2">
                      <select
                        value={String(job.period_start_year)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-black text-sm"
                        onChange={(e) =>
                          handleExperienceChange(
                            index,
                            "period_start_year",
                            e.target.value
                          )
                        }
                      >
                        <option value="0">Year</option>
                        {Array.from(
                          { length: new Date().getFullYear() - 1990 + 1 },
                          (_, i) => new Date().getFullYear() - i
                        ).map((year) => (
                          <option key={year} value={year.toString()}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>
                    {(job.period_start_year < 1990 || job.period_start_year > new Date().getFullYear()) && (
                      <div className="flex flex-row items-center mt-2 space-x-1">
                        <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                        <p className="text-red-600 text-sm">
                          Period start year must be between 1990 and the current year.
                        </p>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-sm text-black mb-1 block">
                      Period End
                    </label>
                    <div className="flex flex-row space-x-2">
                      <select
                        value={job.is_current ? "9999" : job.period_end_year}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-black text-sm"
                        onChange={(e) => {
                          toggleCurrentlyWorking(index, e.target.value === "9999");
                          handleExperienceChange(
                            index,
                            "period_end_year",
                            e.target.value
                          )
                        }}
                      >
                        <option value="9999">Present</option>
                        {Array.from(
                          { length: new Date().getFullYear() - 1990 + 1 },
                          (_, i) => new Date().getFullYear() - i
                        ).map((year) => (
                          <option key={year} value={year.toString()}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>
                    {(job.period_end_year < job.period_start_year) && (
                      <div className="flex flex-rowm items-center mt-2 space-x-1">
                        <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                        <p className="text-red-600 text-sm">
                          Period end year cannot be before period start year.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-black mb-1 block">
                      Company
                    </label>
                    <Input
                      value={job.institution}
                      onChange={(e) =>
                        handleExperienceChange(
                          index,
                          "institution",
                          e.target.value
                        )
                      }
                      className="border-gray-300 text-black"
                    />
                    {job.institution === "" && (
                      <div className="flex flex-row items-center mt-2 space-x-1">
                        <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                        <p className="text-red-600 text-sm">
                          Company name is required.
                        </p>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-sm text-black mb-1 block">
                      Location
                    </label>
                    <Input
                      value={job.location}
                      onChange={(e) =>
                        handleExperienceChange(
                          index,
                          "location",
                          e.target.value
                        )
                      }
                      className="border-gray-300 text-black"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm text-black mb-1 block">
                    Main Achievement
                  </label>
                  <Textarea
                    value={job.description}
                    onChange={(e) =>
                      handleExperienceChange(
                        index,
                        "description",
                        e.target.value
                      )
                    }
                    className="border-gray-300 text-black"
                    rows={5}
                  />
                </div>
              </div>
            ) : (
              <div
                key={index}
                className={cn("relative pl-12", {
                  "pb-8": index < experiences.length - 1,
                })}
              >
                {/* Timeline line - only show for non-last items */}
                {index < experiences.length - 1 && (
                  <div className="absolute left-3 top-1 bottom-0 w-[2px] bg-blue-100 dark:bg-blue-900/30"></div>
                )}

                {/* Icon */}
                <div className="absolute left-0 top-1">
                  <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center z-10 border-2 border-blue-500 dark:border-blue-400">
                    <BriefcaseIcon className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>

                {/* Content */}
                <div>
                  <div className="mb-1 relative">
                    <div className="flex sm:flex-row flex-col flex-wrap items-start sm:items-center gap-2">
                      <h5 className="font-medium text-gray-900 dark:text-gray-100 text-left">
                        {job.role}
                      </h5>
                      {/* Tags moved to be beside the title - filter for work context only */}
                      {job.tags &&
                        job.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {filterTagsByContext(
                              job.tags as TagType[],
                              "work"
                            ).map((tag, tagIndex) => (
                              <ExperienceTag
                                key={tagIndex}
                                type={tag}
                              />
                            ))}
                          </div>
                        )}
                    </div>
                    <span className="text-sm text-gray-500 dark:text-gray-400 sm:absolute sm:right-0 sm:top-0">
                      {job.period_end_year === 9999 ? (
                        <>
                          {job.period_start_year} - Present
                        </>
                      ) : (
                        <>
                          {job.period_start_year} - {job.period_end_year}
                        </>
                      )}
                    </span>
                  </div>
                  {job.institution || job.location && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                      {job.institution}{job.location && ` • ${job.location}`}
                    </p>
                  )}
                  {job.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {job.description}
                    </p>
                  )}

                </div>
              </div>
            )}
          </>
        ))}
      </div>
      {props.isEditing && (
        <Button
          onClick={addExperience}
          variant="ghost"
          className="mt-4 flex items-center text-blue-500 hover:text-blue-700"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Work Experience
        </Button>
      )}
    </div>
  );
}
